import { Badge, Caption1, Input, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { ChevronDown12Regular, MoreHorizontal16Regular } from "@fluentui/react-icons";
import { useEffect, useState } from "react";
import { ConfirmDialog, DialogCheckbox, DialogField } from "@/components/app-dialog";
import { useDataTableStyles } from "@/components/data-table";
import { FuiButton } from "@/components/fui-control";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { InviteDialog } from "@/components/invite-dialog";
import { useDialogLayer } from "@/components/ui/dialog-layer";
import { formatDate } from "@/lib/date-format";
import { cancelMockInvite, expireDueInvites, inviteRemainLabel, isRoleDemotion, reopenMockInvite, resendImpactSentence, resendMockInvite, setMockInviteRole, useMockPlanAccess, type MockMember } from "./mock-plan-access";
import { PersonAvatar } from "./PersonAvatar";
import {
  recordWorkspaceAudit,
  useWorkspaceAuditRows,
} from "./workspace-access";
import {
  applyWorkspacePolicy,
  emailOutsideDomain,
  getWorkspacePolicy,
  policyChangeDetail,
  policyDraftError,
  policyRelaxed,
  type WorkspacePolicy,
} from "./workspace-policy";

const useStyles = makeStyles({
  stack: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    minWidth: 0,
  },
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    minWidth: 0,
    width: "100%",
  },
  card: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "20px",
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  cardHead: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  cardTitle: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
  cardSubtitle: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground3,
  },
  grid2: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "14px",
    "@media (max-width: 540px)": {
      gridTemplateColumns: "1fr",
    },
  },
  actionsBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    paddingTop: "16px",
    borderTopWidth: "1px",
    borderTopStyle: "solid",
    borderTopColor: tokens.colorNeutralStroke2,
  },
  invite: {
    flex: 1,
    minHeight: 0,
  },
  tableScroll: {
    flex: 1,
    minHeight: 0,
  },
  quiet: { color: tokens.colorNeutralForeground3 },
  toolbar: {
    display: "flex",
    justifyContent: "flex-end",
  },
  bad: { color: tokens.colorPaletteRedForeground1 },
  person: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    minWidth: 0,
  },
  personText: { minWidth: 0, flex: 1 },
  name: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
  },
  meta: {
    display: "block",
    maxWidth: "100%",
    color: tokens.colorNeutralForeground3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  remain: {
    display: "block",
    color: tokens.colorNeutralForeground3,
    fontVariantNumeric: "tabular-nums",
  },
  domainRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    minWidth: 0,
    marginTop: "2px",
  },
  pager: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "8px",
    flexShrink: 0,
  },
  range: {
    color: tokens.colorNeutralForeground3,
    fontVariantNumeric: "tabular-nums",
  },
  center: { textAlign: "center" },
  popover: {
    zIndex: 100,
    pointerEvents: "auto",
  },
  roleBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "2px",
    maxWidth: "100%",
    height: "24px",
    padding: "0 4px",
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  menuBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    padding: 0,
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground3,
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
});

type InviteAction = "resend" | "cancel" | "reinvite";

function invitePendingMessage(pending: InvitePending | null) {
  if (!pending) return "";
  if (pending.kind === "resend") return `초대를 다시 보낼까요? ${resendImpactSentence()}`;
  if (pending.kind === "role") return "역할을 낮출까요? 이 워크스페이스에서 할 수 있는 일이 줄어듭니다.";
  return "초대를 취소할까요?";
}

function inviteActions(status: string): { kind: InviteAction; label: string }[] {
  if (status === "초대 중") return [{ kind: "resend", label: "재발송" }, { kind: "cancel", label: "초대 취소" }];
  if (status === "거절" || status === "만료") return [{ kind: "reinvite", label: "다시 초대" }];
  return [];
}

const INVITE_ROLES = ["관리자", "편집자", "뷰어"];
const INVITE_PAGE = 10;

type InvitePending =
  | { kind: "resend" | "cancel"; person: MockMember }
  | { kind: "role"; person: MockMember; role: string };

/** 워크스페이스 설정의 초대·권한·감사. 소유자와 관리자만 연다. */
export function WorkspaceInvitePanel({ workspaceName }: { workspaceName: string }) {
  const styles = useStyles();
  const table = useDataTableStyles();
  const access = useMockPlanAccess();
  const layer = useDialogLayer();
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [pending, setPending] = useState<InvitePending | null>(null);
  useEffect(() => {
    expireDueInvites();
  }, []);
  const total = access.invites.length;
  const pageCount = Math.max(1, Math.ceil(total / INVITE_PAGE));
  const safePage = Math.min(page, pageCount - 1);
  const from = safePage * INVITE_PAGE;
  const rows = access.invites.slice(from, from + INVITE_PAGE);
  const to = Math.min(from + INVITE_PAGE, total);
  return (
    <div className={mergeClasses(styles.stack, styles.invite)}>
      <div className={styles.toolbar}>
        <FuiButton appearance="primary" density="dialog" onClick={() => setOpen(true)}>
          멤버 초대
        </FuiButton>
      </div>
      {total === 0 ? (
        <GhostEmptyState compact title="아직 초대가 없습니다" description="초대를 보내면 수락 전에 여기에 있습니다." />
      ) : (
        <>
          <div className={mergeClasses(table.frame, styles.tableScroll)}>
            <table className={table.table}>
              <thead>
                <tr>
                  <th className={table.th}>이름</th>
                  <th className={table.th} style={{ width: "88px" }}>역할</th>
                  <th className={table.th} style={{ width: "96px" }}>상태</th>
                  <th className={mergeClasses(table.th, styles.center)} style={{ width: "48px" }}>동작</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((item) => {
                  const name = item.name || item.email;
                  const email = item.email && item.email !== name ? item.email : "";
                  const actions = inviteActions(item.status);
                  const outside = emailOutsideDomain(item.email);
                  return (
                    <tr key={item.id}>
                      <td className={table.td}>
                        <div className={styles.person}>
                          <PersonAvatar name={name} size={20} />
                          <div className={styles.personText}>
                            <div className={styles.name} title={name}>{name}</div>
                            {email ? <Caption1 className={styles.meta} title={email}>{email}</Caption1> : null}
                            {outside ? (
                              <span className={styles.domainRow}>
                                <Badge size="small" appearance="tint" color="subtle">도메인 밖</Badge>
                                <Caption1 className={styles.meta} title="허용 도메인과 다릅니다.">허용 도메인과 다릅니다.</Caption1>
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </td>
                      <td className={table.td}>
                        {item.status === "초대 중" ? (
                          <Menu mountNode={layer} positioning={{ strategy: "fixed", position: "below", align: "start" }}>
                            <MenuTrigger disableButtonEnhancement>
                              <button type="button" className={styles.roleBtn} aria-label={`${name} 역할, ${item.role}`}>
                                <span className={styles.name}>{item.role}</span>
                                <ChevronDown12Regular />
                              </button>
                            </MenuTrigger>
                            <MenuPopover className={styles.popover}>
                              <MenuList>
                                {INVITE_ROLES.map((role) => (
                                  <MenuItem
                                    key={role}
                                    disabled={role === item.role}
                                    onClick={() => {
                                      if (role === item.role) return;
                                      if (isRoleDemotion(item.role, role)) setPending({ kind: "role", person: item, role });
                                      else setMockInviteRole(item.id, role);
                                    }}
                                  >
                                    {role}
                                  </MenuItem>
                                ))}
                              </MenuList>
                            </MenuPopover>
                          </Menu>
                        ) : (
                          item.role
                        )}
                      </td>
                      <td className={table.td}>
                        <div className={item.status === "거절" ? styles.bad : undefined}>{item.status}</div>
                        {item.status === "초대 중" && item.expires ? (
                          <Caption1 className={styles.remain}>{inviteRemainLabel(item.expires)}</Caption1>
                        ) : null}
                      </td>
                      <td className={mergeClasses(table.td, styles.center)}>
                        {actions.length === 0 ? (
                          <Caption1 className={styles.quiet}>—</Caption1>
                        ) : (
                          <Menu mountNode={layer} positioning={{ strategy: "fixed", position: "below", align: "end" }}>
                            <MenuTrigger disableButtonEnhancement>
                              <button type="button" className={styles.menuBtn} aria-label={`${name} 동작`}>
                                <MoreHorizontal16Regular />
                              </button>
                            </MenuTrigger>
                            <MenuPopover className={styles.popover}>
                              <MenuList>
                                {actions.map((action) => (
                                  <MenuItem
                                    key={action.kind}
                                    onClick={() => {
                                      if (action.kind === "reinvite") reopenMockInvite(item.id);
                                      else setPending({ kind: action.kind, person: item });
                                    }}
                                  >
                                    {action.label}
                                  </MenuItem>
                                ))}
                              </MenuList>
                            </MenuPopover>
                          </Menu>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {total > INVITE_PAGE ? (
            <div className={styles.pager}>
              <FuiButton appearance="subtle" density="dialog" disabled={safePage === 0} onClick={() => setPage(safePage - 1)}>
                이전
              </FuiButton>
              <Caption1 className={styles.range}>{from + 1}–{to} / {total}</Caption1>
              <FuiButton appearance="subtle" density="dialog" disabled={safePage >= pageCount - 1} onClick={() => setPage(safePage + 1)}>
                다음
              </FuiButton>
            </div>
          ) : null}
        </>
      )}
      <InviteDialog
        open={open}
        workspaceName={workspaceName}
        onClose={() => setOpen(false)}
      />
      <ConfirmDialog
        open={pending != null}
        title={pending?.kind === "resend" ? "초대 재발송" : pending?.kind === "role" ? "역할 변경" : "초대 취소"}
        message={invitePendingMessage(pending)}
        cancelLabel="그대로 두기"
        confirmLabel={pending?.kind === "resend" ? "재발송" : pending?.kind === "role" ? "변경" : "초대 취소"}
        onCancel={() => setPending(null)}
        onConfirm={() => {
          if (pending?.kind === "cancel") cancelMockInvite(pending.person.id);
          if (pending?.kind === "resend") resendMockInvite(pending.person.id);
          if (pending?.kind === "role") setMockInviteRole(pending.person.id, pending.role);
          setPending(null);
        }}
      />
    </div>
  );
}

export function WorkspaceAccessPanel() {
  return (
    <div className={useStyles().container}>
      <WorkspacePolicyForm />
    </div>
  );
}

function WorkspacePolicyForm() {
  const styles = useStyles();
  const [draft, setDraft] = useState<WorkspacePolicy>(() => getWorkspacePolicy());
  const [ask, setAsk] = useState(false);
  const [savedNote, setSavedNote] = useState(false);
  const error = policyDraftError(draft);
  const relaxed = policyRelaxed(getWorkspacePolicy(), draft);

  return (
    <div className={styles.container}>
      {/* 1. 도메인 및 외부 접근 정책 카드 */}
      <div className={styles.card}>
        <div className={styles.cardHead}>
          <div className={styles.cardTitle}>도메인 및 외부 접근 정책</div>
          <Caption1 className={styles.cardSubtitle}>
            워크스페이스에 접근하거나 가입할 수 있는 이메일 도메인 및 외부 공유 범위를 제한합니다.
          </Caption1>
        </div>

        <DialogField label="허용 도메인" hint="비워둘 경우 모든 이메일 도메인 가입이 허용됩니다.">
          <Input
            size="small"
            aria-label="허용 도메인"
            placeholder="예: wavex.io"
            value={draft.domain}
            onChange={(_, data) => {
              setDraft({ ...draft, domain: data.value });
              setSavedNote(false);
            }}
          />
        </DialogField>

        <div style={{ paddingTop: "4px" }}>
          <DialogCheckbox
            label="외부 공유 허용 (외부 게스트 초대 및 읽기 링크 발행)"
            checked={draft.externalShare}
            onChange={(next) => {
              setDraft({ ...draft, externalShare: next });
              setSavedNote(false);
            }}
          />
        </div>
      </div>

      {/* 2. 세션 및 초대 유효 수명 카드 */}
      <div className={styles.card}>
        <div className={styles.cardHead}>
          <div className={styles.cardTitle}>세션 및 초대 유효 기간</div>
          <Caption1 className={styles.cardSubtitle}>
            로그인 세션 유지 기간 및 신규 발송되는 초대장의 만료 기한을 설정합니다.
          </Caption1>
        </div>

        <div className={styles.grid2}>
          <DialogField label="초대 유효 기간 (일)" hint="앞으로 보내는 초대에 적용됩니다. (기본 7일)">
            <Input
              size="small"
              type="number"
              aria-label="초대 유효 기간"
              value={String(draft.inviteDays)}
              onChange={(_, data) => {
                setDraft({ ...draft, inviteDays: Number(data.value) });
                setSavedNote(false);
              }}
            />
          </DialogField>

          <DialogField label="세션 유지 기간 (일)" hint="재인증 없이 로그인을 유지할 일수입니다. (기본 30일)">
            <Input
              size="small"
              type="number"
              aria-label="세션 일수"
              value={String(draft.sessionDays)}
              onChange={(_, data) => {
                setDraft({ ...draft, sessionDays: Number(data.value) });
                setSavedNote(false);
              }}
            />
          </DialogField>
        </div>
      </div>

      {error ? <Caption1 className={styles.bad}>{error}</Caption1> : null}

      <div className={styles.actionsBar}>
        <div>
          {savedNote ? <Caption1 className={styles.quiet}>정책이 저장되었습니다.</Caption1> : null}
        </div>
        <FuiButton
          appearance="primary"
          density="dialog"
          disabled={error != null}
          onClick={() => setAsk(true)}
        >
          정책 저장
        </FuiButton>
      </div>

      <ConfirmDialog
        open={ask}
        title="정책 저장"
        message={relaxed ? "정책이 느슨해집니다. 이 워크스페이스에 적용합니다." : "이 워크스페이스의 보안·공유 정책을 바꿉니다."}
        confirmLabel="저장"
        onCancel={() => setAsk(false)}
        onConfirm={() => {
          const prev = getWorkspacePolicy();
          const detail = policyChangeDetail(prev, draft);
          if (applyWorkspacePolicy(draft) && detail) {
            recordWorkspaceAudit(relaxed ? "정책 완화" : "정책 변경", detail, "나", relaxed ? "critical" : "info");
          }
          setSavedNote(true);
          setAsk(false);
        }}
      />
    </div>
  );
}

export function WorkspaceAuditPanel() {
  const styles = useStyles();
  const table = useDataTableStyles();
  const rows = useWorkspaceAuditRows();

  return (
    <div className={styles.container}>
      <div className={styles.cardHead} style={{ padding: "4px 0 8px" }}>
        <div className={styles.cardTitle}>워크스페이스 감사 로그</div>
        <Caption1 className={styles.cardSubtitle}>
          워크스페이스 내 멤버 초대, 역할 변경, 보안 정책 갱신 등 주요 보안 이벤트를 기록합니다.
        </Caption1>
      </div>

      <div className={table.frame}>
        <table className={table.table} aria-label="워크스페이스 감사">
          <colgroup>
            <col style={{ width: 140 }} />
            <col style={{ width: 100 }} />
            <col style={{ width: 140 }} />
            <col />
          </colgroup>
          <thead>
            <tr>
              <th className={mergeClasses(table.th, table.num)}>일시</th>
              <th className={table.th}>수행자</th>
              <th className={table.th}>이벤트</th>
              <th className={table.th}>상세 내용</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td className={mergeClasses(table.td, table.num)}>{formatDate(row.when, "dateTime")}</td>
                <td className={table.td}>{row.who}</td>
                <td className={table.td}>
                  <Badge size="small" appearance="tint" color={row.severity === "critical" ? "danger" : row.severity === "warning" ? "warning" : "informative"}>
                    {row.label}
                  </Badge>
                </td>
                <td className={table.td}>
                  {row.detail ? (
                    <div className={styles.name} title={row.detail}>{row.detail}</div>
                  ) : (
                    <span className={styles.quiet}>—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
